import { useState } from "react";
import { Play, Pause, Lock, Heart, ListPlus, Trash2, CheckCircle2 } from "lucide-react";
import { coverFor, formatTime } from "@/lib/api";
import { usePlayer } from "@/context/PlayerContext";

export default function TrackCard({
  track,
  list,
  index = 0,
  locked = false,
  isFavorite = false,
  onToggleFavorite,
  playlists = [],
  playlistMemberships = [],
  onAddToPlaylist,
  onRemoveFromPlaylist,
  showPlaylistActions = true,
  activePlaylistId = "",
}) {
  const { current, playing, playTrack, toggle } = usePlayer();
  const [playlistOpen, setPlaylistOpen] = useState(false);
  const isCurrent = current?.id === track.id;
  const isPlaying = isCurrent && playing;
  const membershipLabel = playlistMemberships.length > 1
    ? `${playlistMemberships.length} playlisturi`
    : playlistMemberships[0]?.name || "";

  const play = (e) => {
    e.stopPropagation();
    if (isCurrent) toggle();
    else playTrack(track, list);
  };

  const favorite = (e) => {
    e.stopPropagation();
    onToggleFavorite?.(track);
  };

  const quickAdd = (e) => {
    e.stopPropagation();
    if (activePlaylistId && onAddToPlaylist) {
      onAddToPlaylist(activePlaylistId, track);
      return;
    }
    setPlaylistOpen((v) => !v);
  };

  return (
    <div
      data-testid={`track-card-${track.id}`}
      className="track-card fade-up group cursor-pointer p-2"
      style={{ animationDelay: `${(index % 12) * 20}ms` }}
      onClick={play}
    >
      <div className="flex gap-3">
        <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg sm:h-16 sm:w-16">
          <img src={coverFor(track)} alt={track.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 grid place-items-center bg-black/35">
            <button
              data-testid={`play-track-btn-${track.id}`}
              onClick={play}
              className="grid h-8 w-8 place-items-center rounded-full bg-[#D4A23A] text-black shadow-[0_8px_24px_rgba(212,162,58,0.35)] transition hover:scale-105"
            >
              {isPlaying ? <Pause size={15} fill="currentColor" /> : <Play size={15} fill="currentColor" className="ml-0.5" />}
            </button>
          </div>

          {locked && (
            <div className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-black/70 px-1.5 py-0.5 text-[10px] text-[#D4A23A] backdrop-blur">
              <Lock size={10} /> 30s
            </div>
          )}
          {isCurrent && (
            <div className="absolute bottom-1.5 left-1.5 rounded-full bg-black/70 px-1.5 py-1 text-[#D4A23A] backdrop-blur">
              <span className="eq-bars"><span /><span /><span /><span /></span>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="truncate font-display text-sm font-semibold text-white">{track.title}</h3>
              <p className="truncate text-xs text-zinc-400">{track.artist}</p>
            </div>
            <span className="shrink-0 font-mono text-[11px] text-zinc-500">{formatTime(track.duration)}</span>
          </div>

          <div className="mt-2 flex min-w-0 items-center gap-1.5">
            <span className="max-w-[48%] truncate rounded-full border border-white/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-zinc-400">{track.genre}</span>
            <span className="max-w-[48%] truncate rounded-full border border-white/5 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-zinc-500">{track.mood}</span>
          </div>

          {membershipLabel && (
            <div className="mt-2 flex min-w-0 items-center gap-1.5 rounded-lg border border-[#D4A23A]/25 bg-[#D4A23A]/10 px-2 py-1 text-[11px] font-semibold text-[#D4A23A]">
              <CheckCircle2 size={12} className="shrink-0" />
              <span className="truncate" title={playlistMemberships.map((p) => p.name).join(", ")}>
                In playlist: {membershipLabel}
              </span>
            </div>
          )}

          <div className="relative mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {onToggleFavorite && (
              <button
                data-testid={`favorite-track-btn-${track.id}`}
                onClick={favorite}
                className={`grid h-8 w-8 place-items-center rounded-full transition ${
                  isFavorite ? "bg-[#D4A23A] text-black" : "border border-white/10 text-zinc-300 hover:bg-white/5 hover:text-white"
                }`}
                title={isFavorite ? "Elimina din favorite" : "Adauga la favorite"}
              >
                <Heart size={14} fill={isFavorite ? "currentColor" : "none"} />
              </button>
            )}

            {showPlaylistActions && onAddToPlaylist && (activePlaylistId || playlists.length > 0) && (
              <button
                onClick={quickAdd}
                className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-full border border-white/10 px-3 text-[11px] font-semibold text-zinc-200 transition hover:bg-white/5 hover:text-white sm:flex-none"
                title="Adauga in playlist"
              >
                <ListPlus size={13} /> Playlist
              </button>
            )}

            {onRemoveFromPlaylist && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFromPlaylist();
                }}
                className="grid h-8 w-8 place-items-center rounded-full border border-white/10 text-zinc-400 transition hover:bg-white/5 hover:text-white"
                title="Elimina din playlist"
              >
                <Trash2 size={13} />
              </button>
            )}

            {playlistOpen && playlists.length > 0 && (
              <div className="absolute bottom-9 left-0 z-20 max-h-44 w-full overflow-auto rounded-xl border border-white/10 bg-[#181818] p-1 shadow-2xl sm:w-56">
                {playlists.map((playlist) => (
                  <button
                    key={playlist.id}
                    onClick={() => {
                      onAddToPlaylist(playlist.id, track);
                      setPlaylistOpen(false);
                    }}
                    className="block w-full truncate rounded-lg px-3 py-2 text-left text-xs text-zinc-300 hover:bg-white/5 hover:text-white"
                  >
                    {playlist.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
